import { useState } from 'react';
import { Button, PageShell, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui';
import { useAuthContext } from '@/providers/AuthProvider';
import {
  COLLECTION_MALLS,
  COLLECTION_MALL_LABELS,
  DEFAULT_COLLECTION_MALL,
  type CollectionMall,
} from '../../productCollection/collectionMalls';
import { COMMON_CONSENT_CODES } from '../commonConsents';
import { useConsentTemplatesQuery } from '../hooks/useConsentTemplates';
import { ConsentTemplateCard } from '../components/ConsentTemplateCard';

/** 문구 수정 권한 — catalog PUT 과 같다(조회는 AUDITOR 도). */
export const CONSENT_TEMPLATE_EDIT_ROLES = ['SUPER_ADMIN', 'OPS_ADMIN', 'MD_MANAGER'] as const;

const MallTemplates = ({ mall, canEdit }: { mall: CollectionMall; canEdit: boolean }) => {
  const query = useConsentTemplatesQuery(mall);
  if (query.isPending) {
    return (
      <div role="status" className="space-y-3">
        {COMMON_CONSENT_CODES.map((c) => (
          <Skeleton key={c} className="h-[220px] w-full" />
        ))}
      </div>
    );
  }
  if (query.isError) {
    return (
      <div role="alert" className="flex flex-col items-center gap-2 rounded-lg py-8 text-sm" style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}>
        <p>동의 문구를 불러오지 못했습니다.</p>
        <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
          다시 시도
        </Button>
      </div>
    );
  }
  const byCode = new Map(query.data.map((t) => [t.code, t]));
  return (
    <div className="flex flex-col gap-4">
      {COMMON_CONSENT_CODES.map((code) => {
        const tpl = byCode.get(code) ?? { code, mall, title: '', contentHtml: '', required: true, updatedAt: null };
        // 저장 후 다시 받은 값으로 입력칸을 새로 채운다(updatedAt 이 바뀜).
        return <ConsentTemplateCard key={`${mall}-${code}-${tpl.updatedAt ?? 'new'}`} template={tpl} mall={mall} canEdit={canEdit} />;
      })}
    </div>
  );
};

/**
 * QA-507 [동의 문구 관리] — 상품 공통 동의 4종(개인정보 활용·청약철회 불가·배송일정·미성년자 법정 대리인)의 몰별 문구.
 * 고도몰은 문구가 쇼핑몰 스킨에 고정이고 상품은 사용함/사용안함만 골랐다 — 같은 방식으로 문구는 여기 한 곳, 사용 여부는 상품
 * [추가 입력 · 동의]에서 고른다.
 */
export const ConsentTemplatesPage = () => {
  const { currentAdmin } = useAuthContext();
  const roles = currentAdmin?.roles ?? [];
  const canEdit = CONSENT_TEMPLATE_EDIT_ROLES.some((r) => roles.includes(r));
  const [mall, setMall] = useState<CollectionMall>(DEFAULT_COLLECTION_MALL);

  return (
    <PageShell
      title="동의 문구 관리"
      description="상품 공통 동의 4종의 몰별 문구입니다. 저장하면 이 동의를 사용함으로 둔 모든 상품에 바로 반영됩니다. 상품별 사용 여부는 상품 수정 [추가 입력 · 동의]에서 고릅니다."
    >
      <Tabs value={mall} onValueChange={(v) => setMall(v as CollectionMall)} className="w-full">
        <TabsList className="mb-4">
          {COLLECTION_MALLS.map((m) => (
            <TabsTrigger key={m} value={m} data-testid={`consent-template-mall-${m}`}>
              {COLLECTION_MALL_LABELS[m]}
            </TabsTrigger>
          ))}
        </TabsList>
        {COLLECTION_MALLS.map((m) => (
          <TabsContent key={m} value={m}>
            {m === mall ? <MallTemplates mall={m} canEdit={canEdit} /> : null}
          </TabsContent>
        ))}
      </Tabs>
    </PageShell>
  );
};
